import { connect } from "react-redux";
import { Row, Col } from "react-bootstrap";
import { useDebouncedCallback } from "use-debounce";
import { useEffect, useMemo, useRef, useState } from "react";

import { inventoryActions } from "../../../redux/actions";
import { PRODUCT_TYPES_ENUMS } from "../../../system/globalEnums";
import {
  Topbar,
  SearchField,
  FilterDropdowns,
  CategoriesDropdown,
  DashboardDatePicker,
  ServerPaginationTable,
} from "../../../shared/components";
import {
  CrossIcon,
  SearchIcon,
  BlueBoxIcon,
  ExportCSVIcon,
} from "../../../assets/icons/iconsProvider";
import {
  globalConstants,
  inventoryConstants,
  buttonNameConstants,
  categoryDropdownConstants,
  defaultGridState,
  TABLE_IDS,
} from "../../../constants";
import {
  downloadCSV,
  getDefaultFilterType,
  LoadingIndicator,
  isSpinnerEnabled,
  prepareFilterForInventoryHistoryPaginationCall,
  isButtonDisable,
  getRowId,
  getRowStyle,
  getTimeFrame,
  getInventoryHistoryTableColumns,
} from "../../../shared/utility";

const History = (props) => {
  const {
    inventory,
    spinnerArray,
    currentStore,
    inventoryHistory,
    customTypes,
    customCategories,
    customSubCategories,
    getPaginatedInventoryHistory,
  } = props;
  const gridRef = useRef();
  const [typeSearchText, setTypeSearchText] = useState("");
  const [gridState, setGridState] = useState(defaultGridState);
  const [isTypeSearchLoading, setIsTypeSearchLoading] = useState(false);
  const [debouncedTypeSearchText, setDebouncedTypeSearchText] = useState("");
  const [timeFrame, setTimeFrame] = useState({
    start: "",
    end: "",
    duration: "daily",
  });
  const [transactionType, setTransactionType] = useState({
    label: globalConstants.ALL,
    value: globalConstants.ALL,
  });
  const [category, setCategory] = useState("");
  const [productType, setProductType] = useState(
    getDefaultFilterType(customTypes, PRODUCT_TYPES_ENUMS.VIDEO_GAME)
  );
  const [productSubcategory, setProductSubCategory] = useState("");

  const skeletonArray = Array(10).fill({});
  const updateGridState = (gridState) => {
    getPaginatedInventoryHistory(currentStore?.id, gridState);
    setGridState(gridState);
  };

  const handleDebouncedTypeSearch = useDebouncedCallback((value) => {
    setTypeSearchText(value);
    updateGridState({ ...gridState, searchQuery: value, pageIndex: 1 });
  }, 1000);

  const handleTypeSearchChange = (value) => {
    setIsTypeSearchLoading(true);
    setDebouncedTypeSearchText(value);
    handleDebouncedTypeSearch(value);
  };

  const filterColumnforExport = (allData) => {
    return allData.map((row) => {
      const exportObject = {
        category: row?.product?.category,
        quantity: row?.quantity,
        processedBy: row?.processedBy,
        date: row?.createdOn,
        transactionType: row?.TransactionType,
      };
      return exportObject;
    });
  };
  const handleGetTimeFrame = (value) => {
    getTimeFrame(value, timeFrame, setTimeFrame);
  };

  const handleExportButtonClick = () => {
    downloadCSV(
      filterColumnforExport(inventoryHistory),
      inventoryConstants.EXPORT_FILE_NAME
    );
  };

  const HistoryColDefs = useMemo(
    () => getInventoryHistoryTableColumns({ currentStore }),
    // eslint-disable-next-line
    [currentStore]
  );

  useEffect(() => {
    if (currentStore && timeFrame.start && timeFrame.end) {
      updateGridState({
        ...gridState,
        filters: prepareFilterForInventoryHistoryPaginationCall(
          productType,
          category,
          transactionType,
          timeFrame.start,
          timeFrame.end,
          productSubcategory
        ),
        pageIndex: 1,
      });
    }
    // eslint-disable-next-line
  }, [
    timeFrame,
    currentStore,
    transactionType,
    category,
    productType,
    productSubcategory,
  ]);
  const clearSearchText = () => {
    setTypeSearchText("");
    setDebouncedTypeSearchText("");
    handleTypeSearchChange("");
  };
  const gridData = useMemo(() => {
    let filteredData = inventoryHistory?.inventoryHistory || [];
    setGridState({
      ...gridState,
      pageIndex: inventoryHistory.pageNumber || defaultGridState?.pageIndex,
      totalRecords:
        inventoryHistory.totalRecords || defaultGridState?.totalRecords,
      pageEndRecordNumber:
        inventoryHistory.pageEndRecordNumber ||
        defaultGridState?.pageEndRecordNumber,
      pageStartRecordNumber:
        inventoryHistory.pageStartRecordNumber |
        defaultGridState?.pageStartRecordNumber,
    });
    setIsTypeSearchLoading(false);
    return filteredData;
    // eslint-disable-next-line
  }, [inventory.inventoryHistory]);

  useEffect(() => {
    setProductType(getDefaultFilterType(customTypes, PRODUCT_TYPES_ENUMS.ALL));
  }, [customTypes]);

  return (
    <>
      <Topbar
        exportButton={true}
        title="Inventory History"
        IconImage={ExportCSVIcon}
        label={buttonNameConstants.EXPORT_CSV}
        handleClick={() => handleExportButtonClick()}
      ></Topbar>
      <Row className="m-0">
        <Col
          md={12}
          className="d-flex justify-conetent-center align-items-center gap-2"
        >
          <div className={`w-100 d-flex align-items-center gap-3 `}>
            <DashboardDatePicker
              label="Date"
              getTimeFrame={handleGetTimeFrame}
            />{" "}
            <CategoriesDropdown
              label="Activity Type"
              isClearable={false}
              placeholder="Activity Type"
              noOptionsMessage="No Activity Type Found"
              options={inventoryConstants.ACTIVITY_TYPE}
              className={"filter-dropdowns-activity-type "}
              SelectIconImage={categoryDropdownConstants.BEND_ICON}
              handleChange={(e) => {
                setTransactionType(
                  e == null
                    ? {
                        label: globalConstants.ALL,
                        value: globalConstants.ALL,
                      }
                    : e
                );
              }}
              defaultValue={{
                value: PRODUCT_TYPES_ENUMS.ALL,
                label: "All",
              }}
            />
          </div>
        </Col>
        <Col
          md={12}
          className="d-flex justify-conetent-center align-items-center gap-2"
        >
          <div className={`w-100 d-flex align-items-center gap-3`}>
            <SearchField
              value={debouncedTypeSearchText}
              label="Search"
              placeHolder="Type or Scan"
              IconImage={SearchIcon}
              BlueBoxIcon={BlueBoxIcon}
              clearSearchText={clearSearchText}
              CrossIcon={typeSearchText ? CrossIcon : ""}
              onChange={(e) => handleTypeSearchChange(e.target.value)}
              LoadingIndicator={isTypeSearchLoading && LoadingIndicator}
            />
            <FilterDropdowns
              customTypes={customTypes}
              customCategories={customCategories}
              customSubCategories={customSubCategories}
              setSearchProductType={setProductType}
              setSearchProductCategory={setCategory}
              setSearchProductSubCategory={setProductSubCategory}
              defaultProductType={PRODUCT_TYPES_ENUMS.ALL}
              isDisabledAll={isButtonDisable(
                spinnerArray,
                inventoryConstants.GET_ALL_INVENTORY
              )}
            />
          </div>
        </Col>
      </Row>
      <Row className="m-0 w-100 h-100 user-screen-wrapper pt-3 p-0">
        <ServerPaginationTable
          colDefs={HistoryColDefs}
          gridRef={gridRef}
          gridData={
            isSpinnerEnabled(
              spinnerArray,
              inventoryConstants.GET_INVENTORY_HISTORY
            )
              ? skeletonArray
              : gridData
          }
          gridState={gridState}
          getRowId={getRowId}
          singleClickEdit={false}
          getRowStyle={getRowStyle}
          updateGridState={updateGridState}
          paginationPageSize={gridState.pageSize}
          pageSizeOptions={gridState.pageSizeOptions}
          isDisablePaginationActions={isSpinnerEnabled(
            spinnerArray,
            inventoryConstants.GET_INVENTORY_HISTORY
          )}
          tableHeight={`calc(100vh - 290px)`}
          tableId={TABLE_IDS.INVENTORY_HISTORY}
        />
      </Row>
    </>
  );
};

//-------Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  inventory: state.inventory,
  currentStore: state.store.currentStore,
  spinnerArray: state.system.spinnerArray,
  customTypes: state.itemOrganization.customTypes,
  inventoryHistory: state.inventory.inventoryHistory,
  customCategories: state.itemOrganization.customCategories,
  customSubCategories: state.itemOrganization.customSubCategories,
});

//-------Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getPaginatedInventoryHistory: (storeId, gridState) =>
    dispatch(inventoryActions.getPaginatedInventoryHistory(storeId, gridState)),
});

export default connect(mapStateToProps, mapDispatchToProps)(History);
